import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Switch,
  Tag, Typography, Upload, App,
} from 'antd'
import {
  PlusOutlined, EditOutlined, UploadOutlined, UserAddOutlined,
  TeamOutlined,
  CheckCircleOutlined, FolderOpenOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { employeesApi } from '@/features/hr/api'
import { contractorApi } from '@/features/hr/contractorApi'
import apiClient from '@/shared/api/client'
import { departmentsApi } from '@/features/masters/departments/api'
import { plantsApi } from '@/features/masters/plants/api'
import {
  hrImportApi, employeeUserApi, designationsApi, reportingCandidatesApi,
  type ImportPreview,
} from '@/features/hr/enablementApi'
import { EMPLOYEE_CATEGORIES, type Employee } from '@/shared/types/hr'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { displayEmpCode, isDraftEmpCode } from '@/shared/utils/empCode'
import dayjs from 'dayjs'

// CATEGORIES now imported from shared/types/hr as EMPLOYEE_CATEGORIES
const SHIFT_TYPES = ['ROTATING', 'GENERAL', 'FIXED_A', 'FIXED_B', 'FIXED_C']
const SALARY_TYPES = ['MONTHLY', 'DAILY', 'PIECE_RATE']

export default function EmployeesPage() {
  const navigate = useNavigate()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [deptFilter, setDeptFilter] = useState<string | undefined>()
  const [catFilter, setCatFilter] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Employee | null>(null)

  const canCreate = usePermission('employee.create')
  const canUpdate = usePermission('employee.update')
  const canCreateUser = usePermission('user.create')
  const { plantId } = usePlantId()

  // Sprint 0081 — bulk import state
  const [importOpen, setImportOpen] = useState(false)
  // Sprint 0150 — Bulk host authorisation modal
  const [bulkHostOpen, setBulkHostOpen] = useState(false)
  const [bulkDesignations, setBulkDesignations] = useState<string[]>([])
  const [bulkDepartmentIds, setBulkDepartmentIds] = useState<string[]>([])
  const [bulkCanHost, setBulkCanHost] = useState(true)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)

  // Sprint 0081 — designation picklist
  const { data: designations } = useQuery({
    queryKey: ['hr-designations'],
    queryFn: () => designationsApi.list(),
    staleTime: 5 * 60_000,
  })

  // Contractor Foundation — contractor picklist for linking contract workers
  const { data: contractors = [] } = useQuery({
    queryKey: ['contractors-picker'],
    queryFn: () => contractorApi.list(),
    staleTime: 5 * 60_000,
  })

  // Reporting-supervisor candidates per the org chart — driven by the
  // designation + plant currently selected in the form.
  const watchedDesignation = Form.useWatch('designation', form) as string | undefined
  const watchedPlant = Form.useWatch('plant_id', form) as string | undefined
  const { data: reportingData } = useQuery({
    queryKey: ['hr-reporting-candidates', watchedPlant, watchedDesignation, editing?.id],
    queryFn: () => reportingCandidatesApi.get({
      plant_id: watchedPlant!,
      designation_code: watchedDesignation || undefined,
      exclude_employee_id: editing?.id,
    }),
    enabled: !!watchedPlant,
  })
  const supervisorOptions = (reportingData?.candidates ?? [])
    .map((e) => ({ value: e.id, label: `${displayEmpCode(e.emp_code)} · ${e.emp_name}${e.designation ? ` (${e.designation})` : ''}` }))

  // Sprint 0081 — Create User from Employee
  const [userCreatedModal, setUserCreatedModal] = useState<{
    open: boolean
    username?: string | null
    temp_password?: string | null
    role_assigned?: string | null
    was_existing?: boolean
  }>({ open: false })
  const createUserMutation = useMutation({
    mutationFn: (employeeId: string) => employeeUserApi.createUserForEmployee(employeeId),
    onSuccess: (r) => {
      setUserCreatedModal({
        open: true,
        username: r.username,
        temp_password: r.temp_password,
        role_assigned: r.role_assigned,
        was_existing: r.was_existing,
      })
      qc.invalidateQueries({ queryKey: ['employees'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Could not create user')
    },
  })

  const { data, isLoading } = useQuery({
    queryKey: ['employees', page, pageSize, plantFilter, deptFilter, catFilter, search],
    queryFn: () => employeesApi.list({
      page, size: pageSize,
      plant_id: plantFilter || undefined,
      department_id: deptFilter || undefined,
      category: catFilter || undefined,
      search: search || undefined,
    }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })

  const { data: deptsData } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500 }),
  })

  const plantOptions = plantsData?.items.map((p) => ({ label: p.name, value: p.id })) ?? []
  const deptOptions = deptsData?.items.map((d) => ({ label: d.name, value: d.id })) ?? []

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ is_active: true, category: 'PERMANENT', shift_type: 'ROTATING', salary_type: 'MONTHLY', basic_salary: 0 })
    setDrawerOpen(true)
  }

  const openEdit = (record: Employee) => {
    setEditing(record)
    form.setFieldsValue({
      ...record,
      joining_date: dayjs(record.joining_date),
      basic_salary: Number(record.basic_salary),
    })
    setDrawerOpen(true)
  }

  // Sprint 0150 — Bulk host authorisation
  const { data: hostOptions, refetch: refetchHostOptions } = useQuery({
    queryKey: ['host-filter-options'],
    queryFn: async () => (await apiClient.get<{
      designations: { designation: string; employee_count: number; host_count: number }[]
      departments: { department_id: string; department_name: string; employee_count: number; host_count: number }[]
    }>('/hr/employees/host-filter-options')).data,
    enabled: bulkHostOpen,
  })
  const bulkHostMut = useMutation({
    mutationFn: (body: {
      designations: string[]
      department_ids: string[]
      can_host: boolean
    }) => apiClient.post<{ affected: number }>(
      '/hr/employees/bulk-set-host', body,
    ).then((r) => r.data),
    onSuccess: (data) => {
      message.success(
        `${data.affected} employee${data.affected === 1 ? '' : 's'} ${bulkCanHost ? 'authorized as hosts' : 'removed from host list'}.`,
      )
      qc.invalidateQueries({ queryKey: ['employees'] })
      qc.invalidateQueries({ queryKey: ['hosts'] })
      refetchHostOptions()
      setBulkHostOpen(false)
      setBulkDesignations([])
      setBulkDepartmentIds([])
    },
    onError: (e: { response?: { data?: { detail?: string } } }) =>
      message.error(e?.response?.data?.detail ?? 'Bulk update failed'),
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = { ...values, joining_date: (values.joining_date as dayjs.Dayjs)?.format('YYYY-MM-DD') }
      return editing ? employeesApi.update(editing.id, payload) : employeesApi.create(payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] })
      message.success(editing ? 'Employee updated' : 'Employee created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    {
      title: 'Code', dataIndex: 'emp_code', key: 'emp_code', width: 130,
      render: (v: string) =>
        isDraftEmpCode(v)
          ? <Typography.Text type="secondary" italic>{displayEmpCode(v)}</Typography.Text>
          : (v || '—'),
    },
    { title: 'Name', dataIndex: 'emp_name', key: 'emp_name' },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name' },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    { title: 'Designation', dataIndex: 'designation', key: 'designation', render: (v?: string) => v ?? '—' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Salary Type', dataIndex: 'salary_type', key: 'salary_type' },
    {
      title: 'Status', dataIndex: 'is_active', key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      // Sprint 0081 — show "User linked" badge so HR knows who needs an ERP login
      title: 'ERP Login', dataIndex: 'user_id', key: 'user_id',
      render: (uid?: string | null) =>
        uid
          ? <Tag color="blue" icon={<CheckCircleOutlined />}>Linked</Tag>
          : <Tag>Not Linked</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 320,
      // onCell stops propagation so button clicks don't bubble to the row
      // (which would also navigate to the profile).
      onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
      render: (_: unknown, record: Employee) => (
        <Space size={4} wrap>
          {/* Primary action — opens the full tabbed profile that includes
              Attendance / Salary / Statutory / Bank / Nominee / Skill etc.
              All fields editable, same /joining/{id} PATCH endpoint. */}
          <Button
            size="small"
            type="primary"
            icon={<FolderOpenOutlined />}
            onClick={() => navigate(`/hr/employees/${record.id}`)}
          >
            Open Profile
          </Button>
          {canUpdate && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
              title="Quick-edit basics. For attendance / salary / statutory / bank, use Open Profile."
            >
              Quick Edit
            </Button>
          )}
          {canCreateUser && !record.user_id && (
            <Button
              size="small"
              icon={<UserAddOutlined />}
              loading={createUserMutation.isPending}
              onClick={() => createUserMutation.mutate(record.id)}
            >
              Create User
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Employee Master"
        subtitle="Manage employee records · Bulk import via Excel · Generate ERP login from any employee"
        extra={
          <Space>
            {canCreate && (
              <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                Import Excel
              </Button>
            )}
            {/* Sprint 0150 — Bulk Authorize Hosts by designation/department */}
            {canCreate && (
              <Button
                icon={<TeamOutlined />}
                onClick={() => setBulkHostOpen(true)}
              >
                Bulk Authorize Hosts
              </Button>
            )}
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New Employee
              </Button>
            )}
          </Space>
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Select allowClear placeholder="Filter by plant" options={plantOptions} onChange={(v) => { setPlantFilter(v); setPage(1) }} style={{ width: 180 }} />
        <Select allowClear placeholder="Filter by dept" options={deptOptions} onChange={(v) => { setDeptFilter(v); setPage(1) }} style={{ width: 180 }} />
        <Select allowClear placeholder="Category" options={EMPLOYEE_CATEGORIES.map(c => ({ label: c, value: c }))} onChange={(v) => { setCatFilter(v); setPage(1) }} style={{ width: 150 }} />
        <Input.Search placeholder="Search name..." allowClear onSearch={(v) => { setSearch(v); setPage(1) }} style={{ width: 200 }} />
      </div>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }}
        // Click anywhere in a row to open the profile. Button clicks in the
        // Actions column stop propagation so they still work as expected.
        onRow={(record) => ({
          onClick: () => navigate(`/hr/employees/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />

      <FormDrawer title={editing ? 'Edit Employee' : 'New Employee'} open={drawerOpen} onClose={() => setDrawerOpen(false)} onSubmit={onSubmit} loading={saveMutation.isPending} form={form}>
        <Form.Item name="emp_name" label="Full Name" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}><Select options={plantOptions} placeholder="Select plant" /></Form.Item>
        <Form.Item name="department_id" label="Department" rules={[{ required: true }]}><Select options={deptOptions} placeholder="Select department" /></Form.Item>
        <Form.Item
          name="designation"
          label="Designation"
          tooltip="Pick from standard list. Free text still allowed for legacy / custom roles."
        >
          <Select
            showSearch
            allowClear
            placeholder="Pick designation (or type to search)"
            options={(designations ?? []).map((d) => ({
              label: `${d.designation_name} (${d.designation_code})`,
              value: d.designation_code,
            }))}
            filterOption={(input, opt) =>
              ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          name="reporting_supervisor_id"
          label="Reporting Supervisor"
          tooltip="Candidates are the people at the parent designation in the org chart. Powers activation, the org chart, and supervisor reports."
          extra={reportingData
            ? (reportingData.filtered
                ? `Showing ${reportingData.parent_designation_name ?? 'parent role'} — per org chart.`
                : 'Pick a Designation above to filter supervisors by the org chart.')
            : undefined}
        >
          <Select
            showSearch
            allowClear
            placeholder="Pick reporting supervisor"
            options={supervisorOptions}
            filterOption={(input, opt) =>
              ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          name="contractor_id"
          label="Contractor"
          tooltip="Link a contract worker to their contractor. Leave blank for permanent / direct employees."
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Link to a contractor (contract workers)"
            options={contractors.map((c) => ({ value: c.id, label: c.contractor_name }))}
          />
        </Form.Item>
        <Form.Item name="joining_date" label="Joining Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
        <Form.Item name="category" label="Category" rules={[{ required: true }]}><Select options={EMPLOYEE_CATEGORIES.map(c => ({ label: c, value: c }))} /></Form.Item>
        <Form.Item name="shift_type" label="Shift Type" rules={[{ required: true }]}><Select options={SHIFT_TYPES.map(s => ({ label: s, value: s }))} /></Form.Item>
        <Form.Item name="salary_type" label="Salary Type" rules={[{ required: true }]}><Select options={SALARY_TYPES.map(s => ({ label: s, value: s }))} /></Form.Item>
        <Form.Item name="basic_salary" label="Basic Salary" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} precision={2} /></Form.Item>
        <Form.Item name="qualification" label="Qualification"><Input /></Form.Item>
        <Form.Item name="phone" label="Phone"><Input maxLength={20} /></Form.Item>
        <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
        <Form.Item name="pf_no" label="PF Number"><Input /></Form.Item>
        <Form.Item name="esi_no" label="ESI Number"><Input /></Form.Item>
        <Form.Item name="bank_account" label="Bank Account"><Input /></Form.Item>
        <Form.Item name="ifsc" label="IFSC Code"><Input /></Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
        {/* Sprint 0150 — Visitor approval workflow opt-in. Per spec,
            admin opt-in only (no auto-flag). When ON, this person
            appears in the Gate's "Person to Meet" dropdown and gets
            a phone-ring notification when a visitor punches in. */}
        <Form.Item
          name="can_host_visitors"
          label="Can host visitors"
          valuePropName="checked"
          extra="When ON, this employee appears in the Gate's host dropdown and gets a phone-ring notification when a visitor arrives to meet them."
        >
          <Switch />
        </Form.Item>
      </FormDrawer>

      {/* Sprint 0081 — Bulk Excel import */}
      <Modal
        title="Bulk import employees from Excel"
        open={importOpen}
        onCancel={() => { setImportOpen(false); setImportPreview(null); setImportFile(null) }}
        width={900}
        footer={
          <Space>
            <Button onClick={() => { setImportOpen(false); setImportPreview(null); setImportFile(null) }}>
              Close
            </Button>
            {importFile && !importPreview && (
              <Button
                type="primary"
                onClick={async () => {
                  if (!plantId) { message.error('Pick a plant first'); return }
                  try {
                    const p = await hrImportApi.previewEmployees(importFile, plantId)
                    setImportPreview(p)
                  } catch {
                    message.error('Preview failed')
                  }
                }}
              >
                Preview
              </Button>
            )}
            {importPreview && importPreview.error < importPreview.total && (
              <Button
                type="primary"
                onClick={async () => {
                  if (!importFile || !plantId) return
                  try {
                    const r = await hrImportApi.commitEmployees(importFile, plantId)
                    message.success(
                      `Imported: ${r.created} new, ${r.updated} updated, ${r.skipped} skipped`,
                    )
                    setImportOpen(false)
                    setImportPreview(null)
                    setImportFile(null)
                    qc.invalidateQueries({ queryKey: ['employees'] })
                  } catch {
                    message.error('Import failed')
                  }
                }}
              >
                Commit {importPreview.ok + importPreview.update} rows
              </Button>
            )}
          </Space>
        }
      >
        <Typography.Paragraph>
          <Button
            type="link"
            icon={<UploadOutlined rotate={180} />}
            href="/employee_master_template.xlsx"
            target="_blank"
          >
            Download Excel template
          </Button>
          <Typography.Text type="secondary"> — pre-filled with examples, valid values, and your live department codes. Hand to HR to fill in.</Typography.Text>
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Excel/CSV columns (any order): <code>emp_code, emp_name, department_code, designation, category, shift_type, joining_date, phone, email, aadhaar_no, bank_account, ifsc_code, pf_no, esi_no, is_active</code>.
          Department must exist in master. Duplicate emp_code = update.
        </Typography.Paragraph>
        <Upload
          beforeUpload={(file) => {
            setImportFile(file)
            setImportPreview(null)
            return false  // prevent auto-upload
          }}
          maxCount={1}
          accept=".xlsx,.xls,.csv"
        >
          <Button icon={<UploadOutlined />}>Select file</Button>
        </Upload>
        {importFile && (
          <Typography.Text style={{ display: 'block', marginTop: 8 }}>
            Selected: <strong>{importFile.name}</strong>
          </Typography.Text>
        )}

        {importPreview && (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
              <Tag color="green">{importPreview.ok} new</Tag>
              <Tag color="blue">{importPreview.update} update</Tag>
              <Tag color="red">{importPreview.error} error</Tag>
              <Tag>{importPreview.total} total</Tag>
            </Space>
            <DataTable
              rowKey={(r: { row_no: number }) => `r${r.row_no}`}
              size="small"
              pagination={{ pageSize: 10 }}
              dataSource={importPreview.rows}
              columns={[
                { title: 'Row', dataIndex: 'row_no', key: 'row_no', width: 60 },
                {
                  title: 'Status', dataIndex: 'status', key: 'status', width: 90,
                  render: (s: string) => (
                    <Tag color={s === 'OK' ? 'green' : s === 'UPDATE' ? 'blue' : 'red'}>{s}</Tag>
                  ),
                },
                {
                  title: 'Emp code', key: 'emp_code', width: 110,
                  render: (_: unknown, r: unknown) =>
                    (r as { data: { emp_code: string } }).data.emp_code || '—',
                },
                {
                  title: 'Name', key: 'emp_name',
                  render: (_: unknown, r: unknown) =>
                    (r as { data: { emp_name: string } }).data.emp_name || '—',
                },
                {
                  title: 'Dept', key: 'dept',
                  render: (_: unknown, r: unknown) =>
                    (r as { data: { department_label: string } }).data.department_label || '—',
                },
                {
                  title: 'Errors', key: 'errors',
                  render: (_: unknown, r: unknown) => {
                    const errs = (r as { errors: string[] }).errors
                    return errs?.length
                      ? <Typography.Text type="danger">{errs.join(' · ')}</Typography.Text>
                      : <Typography.Text type="secondary">—</Typography.Text>
                  },
                },
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Sprint 0081 — Created user info */}
      <Modal
        title="User created"
        open={userCreatedModal.open}
        onCancel={() => setUserCreatedModal({ open: false })}
        footer={[
          <Button key="ok" type="primary" onClick={() => setUserCreatedModal({ open: false })}>
            Done
          </Button>,
        ]}
      >
        {userCreatedModal.was_existing ? (
          <Typography.Paragraph>
            User already linked: <strong>{userCreatedModal.username}</strong>
          </Typography.Paragraph>
        ) : (
          <>
            <Typography.Paragraph>
              ERP login created. Share these credentials with the employee.
              They should change the password on first login.
            </Typography.Paragraph>
            <div style={{ background: '#f6ffed', padding: 12, borderRadius: 6 }}>
              <div><strong>Username:</strong> {userCreatedModal.username}</div>
              <div><strong>Temp password:</strong> <code>{userCreatedModal.temp_password}</code></div>
              {userCreatedModal.role_assigned && (
                <div><strong>Role:</strong> {userCreatedModal.role_assigned}</div>
              )}
            </div>
            <Typography.Paragraph type="warning" style={{ marginTop: 12 }}>
              This password is shown <strong>only once</strong>. Copy it before closing.
            </Typography.Paragraph>
          </>
        )}
      </Modal>

      {/* Sprint 0150 — Bulk Authorize Hosts modal */}
      <Modal
        title="Bulk Authorize Visitor Hosts"
        open={bulkHostOpen}
        onCancel={() => setBulkHostOpen(false)}
        onOk={() => bulkHostMut.mutate({
          designations: bulkDesignations,
          department_ids: bulkDepartmentIds,
          can_host: bulkCanHost,
        })}
        okText={bulkCanHost ? 'Authorize as hosts' : 'Remove host status'}
        okButtonProps={{
          loading: bulkHostMut.isPending,
          danger: !bulkCanHost,
          disabled: bulkDesignations.length === 0 && bulkDepartmentIds.length === 0,
        }}
        width={620}
      >
        <Typography.Paragraph type="secondary">
          Flag every active employee matching the chosen designations / departments.
          Count next to each option shows: total employees (already authorized as hosts).
        </Typography.Paragraph>
        <Form layout="vertical">
          <Form.Item label="Designations">
            <Select
              mode="multiple"
              placeholder="Pick one or more designations (e.g. Manager, Director, GM)"
              value={bulkDesignations}
              onChange={setBulkDesignations}
              allowClear
              showSearch
              optionFilterProp="label"
              options={(hostOptions?.designations ?? []).map((d) => ({
                value: d.designation,
                label: `${d.designation} — ${d.employee_count} employees (${d.host_count} hosts)`,
              }))}
            />
          </Form.Item>
          <Form.Item label="Departments">
            <Select
              mode="multiple"
              placeholder="Pick one or more departments (optional — combines with designations)"
              value={bulkDepartmentIds}
              onChange={setBulkDepartmentIds}
              allowClear
              showSearch
              optionFilterProp="label"
              options={(hostOptions?.departments ?? []).map((d) => ({
                value: d.department_id,
                label: `${d.department_name} — ${d.employee_count} employees (${d.host_count} hosts)`,
              }))}
            />
          </Form.Item>
          <Form.Item label="Action">
            <Space>
              <Switch
                checked={bulkCanHost}
                onChange={setBulkCanHost}
                checkedChildren="Authorize as hosts"
                unCheckedChildren="Remove host status"
              />
              <Typography.Text type="secondary">
                {bulkCanHost
                  ? 'Selected employees will appear in the Gate’s host dropdown.'
                  : 'Selected employees will be removed from the host dropdown.'}
              </Typography.Text>
            </Space>
          </Form.Item>
        </Form>
        <Typography.Paragraph type="warning" style={{ fontSize: 12 }}>
          When designations AND departments are both filled, the filter is intersected
          (e.g. all Managers in HR + Production departments).
        </Typography.Paragraph>
      </Modal>
    </div>
  )
}
